import React, { useMemo } from 'react';
import ShareRewindSlideBase from './ShareRewindSlideBase';
import { RewindStats } from '../../utils/rewindStats';
import { parseISO } from 'date-fns';

interface ShareRewindShipCollageSlideProps {
  year: number;
  stats: RewindStats;
}

const ShareRewindShipCollageSlide = React.forwardRef<HTMLDivElement, ShareRewindShipCollageSlideProps>(
  ({ year, stats }, ref) => {
    // Prioritize ships for collage: first time ships first, then most recent
    const prioritizedShipImages = useMemo(() => {
      const sorted = [...stats.shipImages].sort((a, b) => {
        // First priority: first time ships come first
        if (a.isNewShip && !b.isNewShip) return -1;
        if (!a.isNewShip && b.isNewShip) return 1;
        
        // Second priority: most recent return dates
        const dateA = parseISO(a.returnDate);
        const dateB = parseISO(b.returnDate);
        return dateB.getTime() - dateA.getTime();
      });
      
      return sorted.slice(0, 9); // Limit to 9 for collage
    }, [stats.shipImages]);

    const hasShipImages = prioritizedShipImages.length > 0 && prioritizedShipImages.some(img => img.imageUrl);
    const shipCount = prioritizedShipImages.filter(img => img.imageUrl).length;
    
    // Scale grid and image sizes based on number of ships
    // Make images much larger, especially for 6 ships
    let gridCols = 3;
    let gapSize = 'gap-6';
    let labelSize = 'text-sm';
    let labelPadding = 'p-2';
    
    if (shipCount === 1) {
      gridCols = 1;
      gapSize = 'gap-0';
      labelSize = 'text-3xl';
      labelPadding = 'p-6';
    } else if (shipCount === 2) {
      gridCols = 2;
      gapSize = 'gap-10';
      labelSize = 'text-2xl';
      labelPadding = 'p-4';
    } else if (shipCount === 3) {
      gridCols = 3;
      gapSize = 'gap-8';
      labelSize = 'text-xl';
      labelPadding = 'p-4';
    } else if (shipCount === 4) {
      gridCols = 2;
      gapSize = 'gap-8';
      labelSize = 'text-xl';
      labelPadding = 'p-4';
    } else if (shipCount === 5) {
      gridCols = 3;
      gapSize = 'gap-7';
      labelSize = 'text-lg';
      labelPadding = 'p-3';
    } else if (shipCount === 6) {
      // Use 2 columns for 6 ships (3 rows x 2 columns) to make images much larger
      gridCols = 2;
      gapSize = 'gap-8'; // Large gaps
      labelSize = 'text-3xl'; // Larger labels for better visibility
      labelPadding = 'p-5'; // More padding
    } else {
      // 7-9 ships: use 3 columns but with larger gaps
      gridCols = 3;
      gapSize = 'gap-5';
      labelSize = 'text-base';
      labelPadding = 'p-3';
    }

    return (
      <ShareRewindSlideBase ref={ref} year={year}>
        <div className="flex flex-col h-full">
          {/* Top Section - Title */}
          <div className="flex flex-col items-center justify-center text-center pt-20 pb-8">
            <div className="text-7xl font-bold text-white leading-none mb-4">
              Ships You Sailed
            </div>
            <div className="text-5xl text-white/90 font-medium">
              {stats.shipImages.length} {stats.shipImages.length === 1 ? 'Ship' : 'Ships'} in {year}
            </div>
          </div>

          {/* Middle Section - Ship Collage */}
          <div className={`flex-1 flex items-center justify-center ${
            shipCount === 6 ? 'px-4' : 'px-8' // Less horizontal padding for 6 ships to maximize image size
          }`}>
            {hasShipImages ? (
              <div className={`grid ${gapSize} w-full ${
                shipCount === 6 ? 'max-w-4xl' : // Optimal width for 2-column layout
                shipCount <= 3 ? 'max-w-4xl' :
                'max-w-5xl'
              } ${
                gridCols === 1 ? 'grid-cols-1' : 
                gridCols === 2 ? 'grid-cols-2' : 
                'grid-cols-3'
              }`}>
                {Array.from({ length: Math.min(9, shipCount) }).map((_, index) => {
                  const shipImage = prioritizedShipImages[index];
                  if (shipImage?.imageUrl) {
                    // Determine label size for cruise line (smaller than ship name)
                    const cruiseLineSize = labelSize === 'text-3xl' ? 'text-xl' :
                                          labelSize === 'text-2xl' ? 'text-lg' :
                                          labelSize === 'text-xl' ? 'text-base' :
                                          labelSize === 'text-lg' ? 'text-sm' :
                                          'text-xs';
                    
                    return (
                      <div key={index} className="relative overflow-hidden rounded-lg bg-white/10 aspect-square">
                        <img
                          src={shipImage.imageUrl}
                          alt=""
                          className="w-full h-full object-cover opacity-90"
                          crossOrigin="anonymous"
                        />
                        <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent ${labelPadding}`}>
                          <p className={`text-white ${labelSize} font-medium truncate`}>{shipImage.shipName}</p>
                          <p className={`text-white/90 ${cruiseLineSize} truncate`}>{shipImage.cruiseLine}</p>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={index} className="bg-gradient-to-br from-purple-800/20 to-pink-800/20 rounded-lg aspect-square" />
                  );
                })}
              </div>
            ) : (
              <div className="text-5xl text-white/70 text-center">
                No ship images available
              </div>
            )}
          </div>

          {/* Bottom Section - Branding */}
          <div className="flex flex-col items-center text-center pb-12">
            <div className="text-4xl font-bold text-white leading-none mb-2">
              Cruising Along Rewind {year}
            </div>
          </div>
        </div>
      </ShareRewindSlideBase>
    );
  }
);

ShareRewindShipCollageSlide.displayName = 'ShareRewindShipCollageSlide';

export default ShareRewindShipCollageSlide;

